import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { useLabAssignments, useReleaseLabAssignment } from '../../hooks/lab-hooks';
import { LabAssignment, labRunStateSentiment, LabRunState } from '../../support/labService';
import {
  LabCard,
  LabDate,
  LabPanelState,
  LabProgressBar,
  LabStatusTag,
  labErrorMessage,
} from './LabShared';

export type LabAssignmentsPanelProps = {
  /** Releasing somebody else's pod. Yours you can always release. */
  canSteward?: boolean;
};

const stateSentiment = (state: LabAssignment['state']) => {
  if (state === 'active') return 'success' as const;
  if (state === 'revoked') return 'danger' as const;
  return 'info' as const;
};

/**
 * Who has which pod.
 *
 * The list the backend returns is already scoped: a steward sees the pools they run, everybody
 * else sees their own assignments. So there is no "mine" filter here - there is nothing to
 * filter out.
 *
 * Releasing hands the pod back to the pool. It does not stop the dCloud session and it does
 * not cancel the run, which is why the confirmation says so: releasing a pod somebody is
 * halfway through a lab on loses them nothing except the pod.
 */
const LabAssignmentsPanel: React.FC<LabAssignmentsPanelProps> = ({ canSteward = false }) => {
  const navigate = useNavigate();
  const [liveOnly, setLiveOnly] = useState(true);
  const [releasing, setReleasing] = useState<LabAssignment | null>(null);
  const [reason, setReason] = useState('');
  const [releaseError, setReleaseError] = useState<string | null>(null);

  const { data: assignments, isLoading, error, refetch } = useLabAssignments(liveOnly);
  const release = useReleaseLabAssignment();

  const openRelease = (assignment: LabAssignment) => {
    setReleasing(assignment);
    setReason('');
    setReleaseError(null);
  };

  const handleRelease = async () => {
    if (!releasing) return;

    setReleaseError(null);

    try {
      await release.mutateAsync({ assignmentId: releasing.id, reason: reason.trim() });
      setReleasing(null);
    } catch (exc) {
      setReleaseError(labErrorMessage(exc, 'Could not release the pod.'));
    }
  };

  return (
    <>
      <LabCard
        title="Pod assignments"
        subtitle="Who holds which pod, and how far they have got."
        actions={
          <>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={liveOnly}
                onChange={(evt) => setLiveOnly(evt.target.checked)}
              />
              <span className="text-gray-700">Live only</span>
            </label>
            <StbButton role="default" size="small" onClick={async () => refetch()}>
              Refresh
            </StbButton>
          </>
        }
      >
        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!assignments?.length}
          emptyMessage={
            liveOnly
              ? 'Nobody holds a pod right now.'
              : 'No assignments, live or historical.'
          }
          loadingLabel="Loading assignments..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Assignee</th>
                  <th className="px-3 py-2 font-semibold">Pod</th>
                  <th className="px-3 py-2 font-semibold">State</th>
                  <th className="px-3 py-2 font-semibold">Run</th>
                  <th className="px-3 py-2 font-semibold">Since</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {(assignments || []).map((assignment) => (
                  <tr key={assignment.id} className="border-b border-gray-100 align-top">
                    <td className="px-3 py-2">
                      <div className="font-medium">
                        {assignment.display_name || assignment.assignee_email}
                      </div>
                      {assignment.display_name && (
                        <div className="text-xs text-gray-500">{assignment.assignee_email}</div>
                      )}
                      {assignment.team_label && (
                        <div className="text-xs text-gray-500">{assignment.team_label}</div>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      <div>{assignment.pod_label || assignment.session_key}</div>
                      <div className="font-mono text-xs text-gray-500">
                        {assignment.session_key}
                      </div>
                      <div className="text-xs text-gray-500">
                        {assignment.pool_slug || assignment.source.replace('_', ' ')}
                      </div>
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap">
                      <LabStatusTag
                        label={assignment.state}
                        sentiment={stateSentiment(assignment.state)}
                      />
                      <div className="text-xs text-gray-500">{assignment.session_state}</div>
                      {assignment.release_reason && (
                        <div className="text-xs text-gray-500">{assignment.release_reason}</div>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      {assignment.run ? (
                        <>
                          <LabStatusTag
                            label={assignment.run_state || 'unknown'}
                            sentiment={labRunStateSentiment(
                              (assignment.run_state || undefined) as LabRunState | undefined,
                            )}
                          />
                          <div className="mt-1">
                            <LabProgressBar
                              percent={assignment.progress_percent ?? 0}
                              label={`${assignment.progress_percent ?? 0}% done`}
                            />
                          </div>
                          <div className="mt-1">
                            <LabProgressBar
                              percent={assignment.score_percent ?? 0}
                              label={`${assignment.score_percent ?? 0}% scored`}
                              sentiment={assignment.run_state === 'passed' ? 'success' : 'info'}
                            />
                          </div>
                        </>
                      ) : (
                        <span className="text-xs text-gray-500">Not started</span>
                      )}
                    </td>

                    <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-600">
                      <LabDate value={assignment.assigned_at} />
                      {assignment.released_at && (
                        <div>
                          {'released '}
                          <LabDate value={assignment.released_at} />
                        </div>
                      )}
                    </td>

                    <td className="px-3 py-2">
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {assignment.access_url && (
                          <a
                            className="text-xs text-blue-700 underline"
                            href={assignment.access_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open pod
                          </a>
                        )}

                        {assignment.run && (
                          <StbButton
                            role="request"
                            size="xsmall"
                            onClick={async () => navigate(`/lab/runs/${assignment.run}`)}
                          >
                            Run
                          </StbButton>
                        )}

                        {assignment.state === 'active' && (
                          <StbButton
                            role="delete"
                            size="xsmall"
                            onClick={async () => openRelease(assignment)}
                          >
                            Release
                          </StbButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      <StbModal
        open={!!releasing}
        role="delete"
        title="Release this pod"
        submitLabel="Release"
        submitButtonRole="delete"
        submitDisabled={release.isPending}
        suppressCloseOnSubmit
        onSubmit={handleRelease}
        onClose={async () => setReleasing(null)}
        apiResponseSection={
          releaseError ? <p className="m-0 text-sm text-amber-800">{releaseError}</p> : undefined
        }
      >
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm text-gray-700">
            {`${releasing?.pod_label || releasing?.session_key} goes back to the pool and can be claimed by somebody else.`}
          </p>

          <p className="m-0 text-xs text-gray-500">
            The dCloud session keeps running and the run keeps its progress - only the hold on
            the pod goes away.
          </p>

          <TextField
            label={canSteward ? 'Reason' : 'Reason (optional)'}
            value={reason}
            onChange={(evt) => setReason(evt.target.value)}
            helperText="Recorded on the assignment."
            multiline
            minRows={2}
            fullWidth
          />
        </div>
      </StbModal>
    </>
  );
};

export default LabAssignmentsPanel;
